/**
 * ABRECHNUNG NACH DER VERANSTALTUNG — die Rechenregeln (Konzept ~/maestro-uebergabe/konzept-abrechnung.html § 3,
 * Antoines Entscheidungen vom 28.09.2026).
 *
 * Die Abrechnung ist eine GEORDNETE LISTE von Zeilen; gerechnet wird von oben nach unten. Jede
 * Zeile hat eine Art:
 *   - `leistung`      Menge × Einzelpreis zu einem Steuersatz (aus dem Angebot oder neu).
 *   - `mehrverbrauch` Betrag zu einem Steuersatz; negativ = Minderverbrauch. Nie in der Rabattbasis
 *                     „Leistungen darüber“ — Standard „Rabatt vor Mehrverbrauch“ (Antoine).
 *   - `gutschrift`    fester Abzug (≤ 0) zu einem Steuersatz, z. B. Reklamation.
 *   - `rabatt` / `zuschlag`  in % oder € mit einem Bezug:
 *        `leistungen_darueber` (Standard): die positiven Leistungszeilen OBERHALB der Rabattzeile.
 *                              Kein Rabatt auf Rabatt, kein Rabatt auf Mehrverbrauch.
 *        `zwischensumme`       alles oberhalb, wie es an dieser Stelle steht (nur auf ausdrückliche Wahl).
 *        `zeilen`              nur die ausdrücklich gewählten Leistungs-/Mehrverbrauchszeilen (`bezugZeilen`),
 *                              z. B. nur „Anteil Speisen“ (Stufe 2). Unabhängig von der Position: eine
 *                              Betragszeile hängt von nichts ab, ihr Betrag steht fest, egal wo sie steht.
 *     Der Betrag wird per Largest-Remainder auf die Steuersätze seiner Basis verteilt — dieselbe
 *     Verteilung wie `computeBreakdown`, damit der LexOffice-Beleg (`buildLexInvoice`) auf den Cent
 *     dieselbe Summe zeigt.
 *
 * Alles in ganzen Cent, brutto. Zeilenbetrag = round(Menge_milli × Preis / 1000) je Zeile (wie
 * `lineGross`, per_unit). USt je Satz aus dem Brutto-Topf: `vatFromGross`.
 *
 * Grenzen (Red Team § 7): kein Steuertopf unter 0 (damit auch keine negative Gesamtsumme), ein
 * €-Rabatt höchstens so groß wie seine Basis, ein %-Rabatt höchstens 100 %. Jeder Verstoß ist ein
 * `AbrechnungsFehler` mit der Zeile, an der er entsteht — gerechnet wird dann nicht, geraten auch nicht.
 *
 * Mehrere Rabatte/Zuschläge in beliebiger Reihenfolge rechnet diese Funktion von oben nach unten.
 */
import { largestRemainder, vatFromGross, type VatBucket } from "./index";

export type AbrechnungsZeilenArt = "leistung" | "mehrverbrauch" | "gutschrift" | "rabatt" | "zuschlag";
export type RabattBezug = "leistungen_darueber" | "zwischensumme" | "zeilen";

export interface AbrechnungsZeile {
  id: string;
  art: AbrechnungsZeilenArt;
  /** Betragszeilen (leistung/mehrverbrauch/gutschrift): Menge in Tausendstel, Standard 1000. */
  mengeMilli?: number;
  /** Betragszeilen: Brutto-Einzelpreis in Cent. */
  einzelpreisCents?: number;
  /** Betragszeilen: Steuersatz in Basispunkten (700 = 7 %, 1900 = 19 %). */
  steuersatzBp?: number;
  /** Rabatt/Zuschlag: Prozent in Basispunkten (500 = 5 %). XOR zu `betragCents`. */
  prozentBp?: number | null;
  /** Rabatt/Zuschlag: fester Betrag in Cent (> 0). XOR zu `prozentBp`. */
  betragCents?: number | null;
  /** Rabatt/Zuschlag: worauf er wirkt. Standard `leistungen_darueber`. */
  bezug?: RabattBezug;
  /** Rabatt/Zuschlag mit Bezug `zeilen`: Kennungen der Leistungs-/Mehrverbrauchszeilen, auf die er wirkt. */
  bezugZeilen?: readonly string[] | null;
}

export interface SatzBetrag {
  rateBp: number;
  cents: number;
}

export interface ZeilenErgebnis {
  id: string;
  art: AbrechnungsZeilenArt;
  /** Wirkung der Zeile auf die Summe (Rabatt/Gutschrift/Minderverbrauch negativ). */
  bruttoCents: number;
  /** Dieselbe Wirkung je Steuersatz (summiert exakt zu `bruttoCents`). */
  jeSatz: SatzBetrag[];
  /** Nur Rabatt/Zuschlag: die Basis, auf die er gerechnet wurde. */
  basisCents?: number;
}

export interface AbrechnungsErgebnis {
  zeilen: ZeilenErgebnis[];
  /** Summe aller Leistungs-, Mehr-/Minderverbrauchs- und Gutschriftzeilen. */
  leistungenCents: number;
  /** Summe aller Rabatte (≤ 0). */
  rabattCents: number;
  /** Summe aller Zuschläge (≥ 0). */
  zuschlagCents: number;
  /** Rechnungsbetrag brutto. */
  summeCents: number;
  /** Je Steuersatz, aufsteigend: brutto, netto, USt. */
  jeSatz: VatBucket[];
}

export type AbrechnungsFehlerCode =
  | "ungueltiger_wert"
  | "rabatt_unklar"
  | "rabatt_ueber_basis"
  | "bezug_ungueltig"
  | "gutschrift_positiv"
  | "steuertopf_negativ"
  | "anzahlung_ueber_betrag";

export class AbrechnungsFehler extends Error {
  constructor(
    readonly code: AbrechnungsFehlerCode,
    message: string,
    readonly zeileId?: string,
  ) {
    super(message);
    this.name = "AbrechnungsFehler";
  }
}

function ganzzahl(wert: unknown, was: string, zeileId: string, min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER): number {
  if (typeof wert !== "number" || !Number.isSafeInteger(wert) || wert < min || wert > max) {
    throw new AbrechnungsFehler("ungueltiger_wert", `${was} ist ungültig (${String(wert)}).`, zeileId);
  }
  return wert;
}

const euro = (c: number) => `${(c / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/** Positive Leistungszeilen oberhalb — nie Mehrverbrauch, nie Rabatt/Zuschlag, nie Gutschrift. */
function basisLeistungenDarueber(bisher: readonly ZeilenErgebnis[]): Map<number, number> {
  const basis = new Map<number, number>();
  for (const z of bisher) {
    if (z.art !== "leistung" || z.bruttoCents <= 0) continue;
    for (const s of z.jeSatz) basis.set(s.rateBp, (basis.get(s.rateBp) ?? 0) + s.cents);
  }
  return basis;
}

const istBetragsArt = (art: AbrechnungsZeilenArt) => art === "leistung" || art === "mehrverbrauch" || art === "gutschrift";

/** Betrag einer Leistungs-, Mehrverbrauchs- oder Gutschriftzeile — hängt von keiner anderen Zeile ab. */
function betragsErgebnis(z: AbrechnungsZeile): ZeilenErgebnis {
  const menge = ganzzahl(z.mengeMilli ?? 1000, "Die Menge", z.id, 0);
  const preis = ganzzahl(z.einzelpreisCents, "Der Preis", z.id);
  if (z.steuersatzBp == null) throw new AbrechnungsFehler("ungueltiger_wert", "Diese Zeile hat noch keinen Steuersatz.", z.id);
  const satz = ganzzahl(z.steuersatzBp, "Der Steuersatz", z.id, 0, 10_000);
  const produkt = menge * preis;
  if (!Number.isSafeInteger(produkt)) throw new AbrechnungsFehler("ungueltiger_wert", "Menge × Preis ist zu groß.", z.id);
  // Symmetrisch runden (V1): ein Minderverbrauch rundet wie derselbe Mehrverbrauch, nur negativ.
  const brutto = Math.sign(produkt) * Math.round(Math.abs(produkt) / 1000);
  if (z.art === "gutschrift" && brutto > 0) {
    throw new AbrechnungsFehler("gutschrift_positiv", "Eine Gutschrift mindert den Betrag — bitte als negativen Betrag eintragen.", z.id);
  }
  return { id: z.id, art: z.art, bruttoCents: brutto, jeSatz: brutto === 0 ? [] : [{ rateBp: satz, cents: brutto }] };
}

/**
 * Bezug „bestimmte Zeilen“: die positiven Teile der gewählten Leistungs- und Mehrverbrauchszeilen.
 * Nie ein Rabatt, Zuschlag oder eine Gutschrift (kein Rabatt auf Rabatt), jede Zeile höchstens einmal.
 */
function basisGewaehlteZeilen(r: AbrechnungsZeile, alle: readonly AbrechnungsZeile[]): Map<number, number> {
  const ids = [...new Set(r.bezugZeilen ?? [])];
  if (ids.length === 0) {
    throw new AbrechnungsFehler("bezug_ungueltig", "Dieser Rabatt bezieht sich auf keine Zeile mehr – bitte Zeilen wählen oder den Bezug ändern.", r.id);
  }
  const basis = new Map<number, number>();
  for (const id of ids) {
    const ziel = alle.find((x) => x.id === id);
    if (!ziel) throw new AbrechnungsFehler("bezug_ungueltig", "Eine gewählte Bezugszeile gibt es nicht mehr – bitte den Bezug neu wählen.", r.id);
    if (ziel.art !== "leistung" && ziel.art !== "mehrverbrauch") {
      throw new AbrechnungsFehler("bezug_ungueltig", "Ein Rabatt oder Zuschlag bezieht sich nur auf Leistungen oder Mehrverbrauch, nie auf andere Rabatte oder Gutschriften.", r.id);
    }
    for (const s of betragsErgebnis(ziel).jeSatz) if (s.cents > 0) basis.set(s.rateBp, (basis.get(s.rateBp) ?? 0) + s.cents);
  }
  return basis;
}

/**
 * Rechnet die Abrechnung. Wirft `AbrechnungsFehler`, statt einen falschen Betrag zu liefern.
 */
export function berechneAbrechnung(zeilen: readonly AbrechnungsZeile[]): AbrechnungsErgebnis {
  const ergebnisse: ZeilenErgebnis[] = [];
  const toepfe = new Map<number, number>();
  const buche = (jeSatz: SatzBetrag[]) => {
    for (const s of jeSatz) toepfe.set(s.rateBp, (toepfe.get(s.rateBp) ?? 0) + s.cents);
  };
  const ids = new Set<string>();

  for (const z of zeilen) {
    if (!z.id || ids.has(z.id)) throw new AbrechnungsFehler("ungueltiger_wert", "Jede Zeile braucht eine eindeutige Kennung.", z.id);
    ids.add(z.id);

    if (istBetragsArt(z.art)) {
      const ergebnis = betragsErgebnis(z);
      ergebnisse.push(ergebnis);
      buche(ergebnis.jeSatz);
      continue;
    }

    // Rabatt / Zuschlag
    const prozent = z.prozentBp ?? null;
    const betrag = z.betragCents ?? null;
    if ((prozent === null) === (betrag === null)) {
      throw new AbrechnungsFehler("rabatt_unklar", "Ein Rabatt oder Zuschlag braucht entweder einen Prozentsatz oder einen Betrag.", z.id);
    }
    if (prozent !== null) ganzzahl(prozent, "Der Prozentsatz", z.id, 1, z.art === "rabatt" ? 10_000 : 1_000_000);
    if (betrag !== null) ganzzahl(betrag, "Der Betrag", z.id, 1);

    const bezug = z.bezug ?? "leistungen_darueber";
    const basisJeSatz =
      bezug === "zwischensumme"
        ? new Map([...toepfe].filter(([, c]) => c > 0))
        : bezug === "leistungen_darueber"
          ? basisLeistungenDarueber(ergebnisse)
          : bezug === "zeilen"
            ? basisGewaehlteZeilen(z, zeilen)
            : (() => {
                throw new AbrechnungsFehler("ungueltiger_wert", `Unbekannter Bezug „${String(bezug)}“.`, z.id);
              })();
    const saetze = [...basisJeSatz.keys()].sort((a, b) => a - b);
    const gewichte = saetze.map((r) => basisJeSatz.get(r)!);
    const basis = gewichte.reduce((s, c) => s + c, 0);

    const menge = prozent !== null ? Math.round((basis * prozent) / 10_000) : betrag!;
    if (z.art === "rabatt" && menge > basis) {
      throw new AbrechnungsFehler("rabatt_ueber_basis", `Der Rabatt (${euro(menge)}) ist größer als seine Basis (${euro(basis)}).`, z.id);
    }
    if (menge > 0 && basis <= 0) {
      throw new AbrechnungsFehler("rabatt_ueber_basis", bezug === "zeilen" ? "Die gewählten Zeilen haben keinen Betrag, auf den diese Zeile wirken könnte." : "Über dieser Zeile steht keine Leistung, auf die sie wirken könnte.", z.id);
    }
    const teile = largestRemainder(menge, gewichte, saetze);
    const vorzeichen = z.art === "rabatt" ? -1 : 1;
    const jeSatz = saetze.map((rateBp, i) => ({ rateBp, cents: vorzeichen * teile[i]! })).filter((s) => s.cents !== 0);
    ergebnisse.push({ id: z.id, art: z.art, bruttoCents: vorzeichen * menge, jeSatz, basisCents: basis });
    buche(jeSatz);
  }

  // Kein Steuertopf unter 0 — die Meldung nennt die letzte Zeile, die ihn gesenkt hat.
  for (const [rateBp, cents] of toepfe) {
    if (cents >= 0) continue;
    const schuld = [...ergebnisse].reverse().find((e) => e.jeSatz.some((s) => s.rateBp === rateBp && s.cents < 0));
    throw new AbrechnungsFehler(
      "steuertopf_negativ",
      `Zum Steuersatz ${rateBp / 100} % ergäbe sich ${euro(cents)} — Abzüge sind größer als die Leistungen.`,
      schuld?.id,
    );
  }

  const jeSatz: VatBucket[] = [...toepfe.keys()]
    .sort((a, b) => a - b)
    .map((rateBp) => {
      const grossCents = toepfe.get(rateBp)!;
      const vatCents = vatFromGross(grossCents, rateBp);
      return { rateBp, grossCents, vatCents, netCents: grossCents - vatCents };
    });
  const summe = (art: AbrechnungsZeilenArt[]) => ergebnisse.filter((e) => art.includes(e.art)).reduce((s, e) => s + e.bruttoCents, 0);
  return {
    zeilen: ergebnisse,
    leistungenCents: summe(["leistung", "mehrverbrauch", "gutschrift"]),
    rabattCents: summe(["rabatt"]),
    zuschlagCents: summe(["zuschlag"]),
    summeCents: jeSatz.reduce((s, b) => s + b.grossCents, 0),
    jeSatz,
  };
}

/**
 * Pauschalpreis ohne Aufteilung (Altfälle ohne Positionen, Antoine 28.09.2026): Anteil Getränke
 * (19 %) und Speisen (7 %). Largest-Remainder, damit beide Teile exakt den Betrag ergeben.
 */
export function teileSpeisenGetraenke(betragCents: number, getraenkeanteilBp: number): { speisenCents: number; getraenkeCents: number } {
  ganzzahl(betragCents, "Der Betrag", "", 0);
  ganzzahl(getraenkeanteilBp, "Der Getränkeanteil", "", 0, 10_000);
  const [speisenCents, getraenkeCents] = largestRemainder(betragCents, [10_000 - getraenkeanteilBp, getraenkeanteilBp], [700, 1900]);
  return { speisenCents: speisenCents!, getraenkeCents: getraenkeCents! };
}

export interface Saldo {
  /** Betrag der Schluss-/Vollrechnung: Summe abzüglich der Anzahlungsrechnungen (kann 0 oder negativ sein). */
  rechnungsbetragCents: number;
  /** Schlussrechnung je Steuersatz (nach Abzug der Anzahlungsrechnungen). */
  rechnungJeSatz: SatzBetrag[];
  bezahltCents: number;
  offenCents: number;
  ueberzahlungCents: number;
}

/**
 * Was nach der Abrechnung übrig bleibt. Anzahlungsrechnungen werden je Satz vom Rechnungsbetrag
 * abgezogen (§ 14 Abs. 5 UStG); Zahlungseingänge (netto, nach Erstattungen) mindern den offenen
 * Betrag. Überzahlung wird nur ausgewiesen — was damit geschieht, entscheidet ein Mensch.
 */
export function abrechnungsSaldo(
  ergebnis: Pick<AbrechnungsErgebnis, "summeCents" | "jeSatz">,
  anzahlungsrechnungen: readonly (readonly { rateBp: number; grossCents: number }[])[],
  zahlungsEingaengeCents: readonly number[],
): Saldo {
  const rest = new Map(ergebnis.jeSatz.map((b) => [b.rateBp, b.grossCents]));
  for (const beleg of anzahlungsrechnungen) {
    for (const b of beleg) {
      ganzzahl(b.grossCents, "Eine Anzahlungsrechnung", "");
      rest.set(b.rateBp, (rest.get(b.rateBp) ?? 0) - b.grossCents);
    }
  }
  // Negative Reste je Satz sind zulässig (§ 14 Abs. 5 UStG: Minuszeile je Satz), ebenso ein Gesamtrest
  // von 0 oder darunter (Antoine 28.09.2026: Schlussrechnung MIT Minusbetrag, danach entscheidet der
  // Betrieb über die Überzahlung).
  const bezahlt = zahlungsEingaengeCents.reduce((s, c) => s + ganzzahl(c, "Ein Zahlungseingang", ""), 0);
  const differenz = ergebnis.summeCents - bezahlt;
  return {
    rechnungsbetragCents: [...rest.values()].reduce((s, c) => s + c, 0),
    rechnungJeSatz: [...rest].sort((a, b) => a[0] - b[0]).map(([rateBp, cents]) => ({ rateBp, cents })),
    bezahltCents: bezahlt,
    offenCents: Math.max(0, differenz),
    ueberzahlungCents: Math.max(0, -differenz),
  };
}
